import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  InlineFieldGroup,
  type InlineGroupField,
} from "@/components/inline-field-group";
import { useUpdateManufacturer } from "@/api/hooks";
import { describePaymentTerms } from "@/features/manufacturers/payment-terms";

type NumberValue = string | number | null | undefined;
type TermsType =
  | "none"
  | "net"
  | "discount_net"
  | "prepaid"
  | "due_on_receipt"
  | "proximo"
  | "unknown";

export interface ManufacturerPaymentTerms {
  payment_terms?: string | null;
  payment_terms_type?: string | null;
  payment_terms_discount_percent?: NumberValue;
  payment_terms_discount_days?: number | null;
  payment_terms_net_days?: number | null;
  payment_terms_deposit_percent?: NumberValue;
  payment_terms_proximo_day?: number | null;
}

const TERMS_OPTIONS: Array<{ value: TermsType; label: string }> = [
  { value: "none", label: "Not recorded" },
  { value: "net", label: "Net" },
  { value: "discount_net", label: "Early-pay discount" },
  { value: "prepaid", label: "Prepaid / CIA" },
  { value: "due_on_receipt", label: "Due on receipt / COD" },
  { value: "proximo", label: "Proximo" },
  { value: "unknown", label: "Unknown" },
];

function termsType(value: string | null | undefined): TermsType {
  return TERMS_OPTIONS.some((option) => option.value === value)
    ? (value as TermsType)
    : "none";
}

function numberInput(value: NumberValue): string {
  if (value == null || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

function parsedNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : Number(trimmed);
}

function inRange(
  value: number | null,
  min: number,
  max: number,
  integer: boolean,
): boolean {
  return (
    value !== null &&
    Number.isFinite(value) &&
    value >= min &&
    value <= max &&
    (!integer || Number.isInteger(value))
  );
}

function sameNumber(value: number | null, upstream: NumberValue): boolean {
  if (value === null) return upstream == null || upstream === "";
  return Number(upstream) === value;
}

type TermsKey =
  | "type"
  | "discountPercent"
  | "discountDays"
  | "netDays"
  | "proximoDay"
  | "deposit"
  | "notes";
type TermsDraft = Record<TermsKey, string>;

function termsDraft(terms: ManufacturerPaymentTerms): TermsDraft {
  return {
    type: termsType(terms.payment_terms_type),
    discountPercent: numberInput(terms.payment_terms_discount_percent),
    discountDays: numberInput(terms.payment_terms_discount_days),
    netDays: numberInput(terms.payment_terms_net_days),
    proximoDay: numberInput(terms.payment_terms_proximo_day),
    deposit: numberInput(terms.payment_terms_deposit_percent),
    notes: terms.payment_terms ?? "",
  };
}

const hasTermsType = (draft: TermsDraft) => draft.type !== "none";
const hasDiscountType = (draft: TermsDraft) => draft.type === "discount_net";
const hasNetDaysType = (draft: TermsDraft) =>
  draft.type === "net" || draft.type === "discount_net";
const hasProximoType = (draft: TermsDraft) => draft.type === "proximo";

/** The whole terms record to save: numbers that don't apply to the terms are cleared. */
function termsBody(draft: TermsDraft) {
  const hasDiscount = hasDiscountType(draft);
  return {
    payment_terms_type: hasTermsType(draft) ? draft.type : null,
    payment_terms_discount_percent: hasDiscount
      ? parsedNumber(draft.discountPercent)
      : null,
    payment_terms_discount_days: hasDiscount
      ? parsedNumber(draft.discountDays)
      : null,
    payment_terms_net_days: hasNetDaysType(draft)
      ? parsedNumber(draft.netDays)
      : null,
    payment_terms_proximo_day: hasProximoType(draft)
      ? parsedNumber(draft.proximoDay)
      : null,
    payment_terms_deposit_percent: hasTermsType(draft)
      ? parsedNumber(draft.deposit)
      : null,
    payment_terms: draft.notes.trim() || null,
  };
}

type TermsBody = ReturnType<typeof termsBody>;

function termsValidity(draft: TermsDraft, desired: TermsBody) {
  return {
    discountPercent:
      !hasDiscountType(draft) ||
      inRange(desired.payment_terms_discount_percent, 0.01, 100, false),
    discountDays:
      !hasDiscountType(draft) ||
      inRange(desired.payment_terms_discount_days, 1, 365, true),
    netDays:
      !hasNetDaysType(draft) ||
      inRange(desired.payment_terms_net_days, 1, 365, true),
    proximoDay:
      !hasProximoType(draft) ||
      inRange(desired.payment_terms_proximo_day, 1, 31, true),
    deposit:
      desired.payment_terms_deposit_percent === null ||
      inRange(desired.payment_terms_deposit_percent, 0.01, 100, false),
  };
}

const TERMS_MESSAGES = {
  discountPercent: "Enter a discount percent between 0 and 100.",
  discountDays: "Enter whole days for the discount.",
  netDays: "Enter whole days for Net.",
  proximoDay: "Enter a proximo day from 1 to 31.",
  deposit: "Enter a deposit percent between 0 and 100.",
};

/** What is wrong with the typed terms, said the way the old form's field errors said it. */
function termsProblem(draft: TermsDraft): string | null {
  const validity = termsValidity(draft, termsBody(draft));
  const bad = (Object.keys(validity) as Array<keyof typeof validity>).filter(
    (key) => !validity[key],
  );
  return bad.length ? bad.map((key) => TERMS_MESSAGES[key]).join(" ") : null;
}

function termsChanged(
  desired: TermsBody,
  terms: ManufacturerPaymentTerms,
): boolean {
  return (
    desired.payment_terms_type !== (terms.payment_terms_type ?? null) ||
    !sameNumber(
      desired.payment_terms_discount_percent,
      terms.payment_terms_discount_percent,
    ) ||
    !sameNumber(
      desired.payment_terms_discount_days,
      terms.payment_terms_discount_days,
    ) ||
    !sameNumber(desired.payment_terms_net_days, terms.payment_terms_net_days) ||
    !sameNumber(
      desired.payment_terms_proximo_day,
      terms.payment_terms_proximo_day,
    ) ||
    !sameNumber(
      desired.payment_terms_deposit_percent,
      terms.payment_terms_deposit_percent,
    ) ||
    desired.payment_terms !== (terms.payment_terms?.trim() || null)
  );
}

/** Saves a terms body; defaults to the manufacturer update, sourcing passes its own. */
export interface TermsMutation {
  mutate: (
    body: Record<string, unknown>,
    options: { onSuccess?: () => void; onError?: () => void },
  ) => void;
  isPending: boolean;
}

/**
 * Payment terms as a card, or bare inside another card (a "record payment terms" action). Edited
 * in place like PaymentTermsField (USA-1580); once saved, the summary shows the saved terms until
 * the caller passes new ones.
 */
export function PaymentTermsPanel({
  uuid,
  terms,
  bare = false,
  onSaved,
  mutation,
}: {
  uuid: string;
  terms: ManufacturerPaymentTerms;
  /** Render just the fields, for use inside another card. */
  bare?: boolean;
  onSaved?: () => void;
  mutation?: TermsMutation;
}) {
  const upstreamKey = JSON.stringify(terms);
  const [lastUpstreamKey, setLastUpstreamKey] = useState(upstreamKey);
  const [saved, setSaved] = useState<ManufacturerPaymentTerms | null>(null);

  if (upstreamKey !== lastUpstreamKey) {
    setLastUpstreamKey(upstreamKey);
    setSaved(null);
  }

  const field = (
    <PaymentTermsField
      uuid={uuid}
      terms={saved ?? terms}
      mutation={mutation}
      onSaved={(body) => {
        setSaved(body);
        onSaved?.();
      }}
    />
  );

  if (bare) return <div className="flex">{field}</div>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Payment terms</CardTitle>
      </CardHeader>
      <CardContent className="flex">{field}</CardContent>
    </Card>
  );
}

const TERMS_FIELDS: ReadonlyArray<InlineGroupField<TermsKey>> = [
  { key: "type", label: "Terms", kind: "select", options: TERMS_OPTIONS },
  {
    key: "discountPercent",
    label: "Discount",
    required: true,
    inputMode: "decimal",
    suffix: "%",
    className: "col-span-3",
    hidden: (draft) => !hasDiscountType(draft),
  },
  {
    key: "discountDays",
    label: "Discount if paid within",
    required: true,
    inputMode: "numeric",
    suffix: "days",
    className: "col-span-3",
    hidden: (draft) => !hasDiscountType(draft),
  },
  {
    key: "netDays",
    label: "Due in",
    required: true,
    inputMode: "numeric",
    suffix: "days",
    className: "col-span-3",
    hidden: (draft) => !hasNetDaysType(draft),
  },
  {
    key: "proximoDay",
    label: "Due next month on day",
    required: true,
    inputMode: "numeric",
    className: "col-span-3",
    hidden: (draft) => !hasProximoType(draft),
  },
  {
    key: "deposit",
    label: "Deposit up front",
    inputMode: "decimal",
    suffix: "%",
    className: "col-span-3",
    hidden: (draft) => !hasTermsType(draft),
  },
  { key: "notes", label: "Terms notes", kind: "textarea" },
];

/**
 * A source's payment terms, edited in place from their summary (USA-1536); Save shows once
 * something changed and sends the same whole terms record as the panel.
 */
export function PaymentTermsField({
  uuid,
  terms,
  mutation,
  onSaved,
}: {
  uuid: string;
  terms: ManufacturerPaymentTerms;
  mutation?: TermsMutation;
  /** Called with the saved terms record after a successful save. */
  onSaved?: (body: TermsBody) => void;
}) {
  const manufacturerUpdate = useUpdateManufacturer(uuid);
  const updateMfr: TermsMutation = mutation ?? manufacturerUpdate;
  const [editing, setEditing] = useState(false);

  const save = (next: TermsDraft) => {
    const desired = termsBody(next);
    if (!termsChanged(desired, terms)) {
      setEditing(false);
      return;
    }
    updateMfr.mutate(desired, {
      onSuccess: () => {
        toast.success("Payment terms updated");
        setEditing(false);
        onSaved?.(desired);
      },
      onError: () => toast.error("Failed to update payment terms"),
    });
  };

  return (
    <div className="min-w-0 flex-1">
      <InlineFieldGroup
        label="payment terms"
        fields={TERMS_FIELDS}
        values={termsDraft(terms)}
        invalid={(draft) => {
          const validity = termsValidity(draft, termsBody(draft));
          return (Object.keys(validity) as Array<keyof typeof validity>).filter(
            (key) => !validity[key],
          );
        }}
        notice={(draft) => termsProblem(draft)}
        saveLabel="Save payment terms"
        pending={updateMfr.isPending}
        editing={editing}
        onEditingChange={setEditing}
        onSave={save}
      >
        <span className="block text-sm font-medium">
          {describePaymentTerms(terms)}
        </span>
      </InlineFieldGroup>
    </div>
  );
}
